import style from "./informationHover.module.css";
import { Info, Lightbulb } from "lucide-react";

import { useRef, useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  arrow,
  FloatingArrow,
} from "@floating-ui/react";

type HighlightedTextProps = {
  text: string;
};

const HighlightedText: React.FC<HighlightedTextProps> = ({ text }) => {
  const parts: string[] = text.split(/(`[^`]*`)/g);

  return (
    <p className={style.descriptionText}>
      {parts.map((part, index) => {
        if (/^`[^`]*`$/.test(part)) {
          return (
            <span key={index} style={{ color: "var(--light-blue-icon)", margin: "0px 5px", }}>
              {part.slice(1, -1)}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </p>
  );
};

interface Props {
  description: string;
  lightbulb?: boolean;
}

function InfoHover({ description, lightbulb }: Readonly<Props>) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset({ mainAxis: 10 }), flip(), shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false, delay: { open: 500 } }); // Toggle tooltip on or off on hover
  const focus = useFocus(context); // Toggle tooltip on or off on focus
  const dismiss = useDismiss(context); // Dismiss tooltip when 'esc' is pressed
  const role = useRole(context, {
    // Correct ARIA attributes for screen readers
    role: "tooltip",
  });

  // Merge all interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <div className={style.infoWrapper }>
      <div ref={refs.setReference} {...getReferenceProps()} className={style.infoHover}>
        {lightbulb ? (
          <div className={style.background}>
            <Lightbulb className={style.infoIcon} stroke="var(--light-blue-icon)" strokeWidth="1.5" />
          </div>
        ) : (
          <Info className={style.infoIcon} stroke="var(--text-color)" strokeWidth="1.5" />
        )}
      </div>
      {isOpen && (
        <div ref={refs.setFloating} {...getFloatingProps()} style={floatingStyles} className={style.descriptionBox}>
          <FloatingArrow fill="var(--text-color)" ref={arrowRef} context={context} />
          <HighlightedText text={description} />
        </div>
      )}
    </div>
  );
}

export default InfoHover;
